import type {Component} from 'grapesjs';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {makeOptionListControl} from '../../controls/option-list-control/option-list-control';
import {verticalAlignDescriptor} from '../../controls/option-list-control/option-list-control-descriptors';
import {makeStyleHeader} from '../../controls/style-header/style-header';
import {makeUploadButtonControl} from '../../controls/upload-button-control/upload-button-control';
import {backgroundMediaDescriptor} from '../../controls/upload-button-control/upload-button-control-descriptors';
import {makeSliderControl} from '../../controls/slider-control/slider-control';
import {borderRadiusDescriptor, paddingDescriptor, widthDescriptor} from '../../controls/slider-control/slider-control-descriptors';
import {makeCtaButtonControl} from '../../controls/cta-button-control/cta-button-control';
import {copyPasteStylesDescriptor} from '../../controls/cta-button-control/cta-button-control-descriptors';
import {registerEmailMakerSector} from '../../right-panel-registry';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import {borderStyleControl, conditionalBorderColorControl, conditionalBorderWidthControl} from '../shared/border-controls';
import {bgColorControl} from '../shared/color-controls';
import type {EmailMakerControl} from '../../right-panel-registry.types';

// PMW_CELL is the only styleHeader call site without top-margin spacing.
const stylesHeader: EmailMakerControl = makeStyleHeader('pmwjs_styles', false);

const copyPasteStylesControl = makeCtaButtonControl(copyPasteStylesDescriptor);
const backgroundMediaControl: EmailMakerControl = makeUploadButtonControl(backgroundMediaDescriptor);

const verticalAlignControl = makeOptionListControl(verticalAlignDescriptor);

const widthControl = makeSliderControl(widthDescriptor);
const borderRadiusControl = makeSliderControl(borderRadiusDescriptor);
const paddingControl = makeSliderControl(paddingDescriptor);

/** Cell styling defaults to the global (all-cells) variant. */
registerEmailMakerSector(EMAIL_MAKER_COMPONENT_TYPES.table.PMW_CELL, {
  /** Mirrors the on-canvas badge (register-table-types.ts sets `name` to "Column" for section cells, "Cell" for raw-table cells). */
  title: (component: Component): string => {
    const name = component.get('name');
    return typeof name === 'string' ? name : window.i18next.t('pmwjs_column');
  },
  properties: [
    {control: stylesHeader},
    {control: copyPasteStylesControl},
    {control: backgroundMediaControl, tileMeta: {icon: 'icon-image', labelKey: 'pmwjs_background_media', complexity: TileComplexity.SIMPLE}},
    {control: bgColorControl, tileMeta: {icon: 'icon-fill', labelKey: 'pmwjs_background_color', complexity: TileComplexity.SIMPLE}},
    {control: verticalAlignControl, tileMeta: {icon: 'icon-v-align-center', labelKey: 'pmwjs_vertical_alignment', complexity: TileComplexity.SIMPLE}},
    {control: widthControl, tileMeta: {icon: 'icon-stretch-horizontal', labelKey: 'pmwjs_width', complexity: TileComplexity.SIMPLE}},
    {control: borderStyleControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_border', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderColorControl, tileMeta: {icon: 'icon-colorpicker', labelKey: 'pmwjs_border_color', complexity: TileComplexity.SIMPLE}},
    {control: conditionalBorderWidthControl, tileMeta: {icon: 'icon-stroke', labelKey: 'pmwjs_thickness', complexity: TileComplexity.SIMPLE}},
    {control: borderRadiusControl, tileMeta: {icon: 'icon-corner', labelKey: 'pmwjs_roundness', complexity: TileComplexity.COMPLEX}},
    {control: paddingControl, tileMeta: {icon: 'icon-edge-spacing', labelKey: 'pmwjs_spacing', complexity: TileComplexity.COMPLEX}},
  ],
});
